import { Ref, useEffect, useMemo, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import {
  Combobox,
  TextInput,
  TextInputProps,
  UnstyledButton,
  useCombobox,
  Loader,
} from '@mantine/core'
import { useDebouncedState, useMergedRef } from '@mantine/hooks'
import { CircleAlert, User, UserRoundX, Users } from 'lucide-react'

import { GET_CARRIER_BY_DOT_NUMBER } from 'src/graphql/carriers.graphql'

import ContactOption from './ContactOption'
import { formatContactCount, reachableContacts } from './knownContacts'

export interface ContactAutocompleteProps
  extends Omit<TextInputProps, 'onChange'> {
  dotNumber: string
  onSelect?: (selectedContact) => void
  classNames?: Record<string, string>
  onChange?: (value: string) => void
  // Where this autocomplete is rendered (e.g. 'dashboard'), used to segment
  // the fmcsa_contact* RUM events by surface
  surface?: string
  inputRef?: Ref<HTMLInputElement>
}

// carrierByDotNumber has no companyName: the carrier is named by its DBA or
// legal name
const carrierNameOf = (
  carrier?: {
    dbaName?: string | null
    legalName?: string | null
  } | null
): string => carrier?.dbaName || carrier?.legalName || 'Unknown'

const ContactAutocomplete = ({
  dotNumber,
  onSelect,
  classNames,
  onChange,
  placeholder,
  label,
  disabled = false,
  surface = 'unknown',
  inputRef,
  ...props
}: ContactAutocompleteProps) => {
  const combobox = useCombobox()
  const [inputDebounced, setInputDebounced] = useDebouncedState('', 400)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredContacts, setFilteredContacts] = useState([])
  const [_selectedContact, setSelectedContact] = useState(null)
  const ownInputRef = useRef<HTMLInputElement>(null)
  const mergedInputRef = useMergedRef(ownInputRef, inputRef)

  /** Show every contact for the DOT, whatever the input holds. */
  const openContacts = () => {
    if (disabled || !dotNumber) return
    setSearchTerm('')
    combobox.openDropdown()
  }

  const [
    getCarrierByDotNumber,
    { loading: loadingContacts, data: carrierData, error: contactsError },
  ] = useLazyQuery(GET_CARRIER_BY_DOT_NUMBER, {
    onCompleted: (data) => {
      const allContacts = data?.carrierByDotNumber?.contacts || []
      // A name alone is not a contact method: the list, the count and the
      // events all count only contacts with a phone or an email
      const contacts = reachableContacts(allContacts)
      setFilteredContacts(contacts)

      // Track contact loading results for monitoring
      if (contacts.length === 0) {
        datadogRum.addAction('fmcsa_contacts_empty', {
          component: 'ContactAutocomplete',
          surface,
          carrier_dot_number: dotNumber,
          carrier_name: carrierNameOf(data?.carrierByDotNumber),
          total_contacts_count: allContacts.length,
        })
      } else {
        datadogRum.addAction('fmcsa_contacts_loaded', {
          component: 'ContactAutocomplete',
          surface,
          carrier_dot_number: dotNumber,
          carrier_name: carrierNameOf(data?.carrierByDotNumber),
          contacts_count: contacts.length,
          total_contacts_count: allContacts.length,
        })
      }
    },
    onError: (error) => {
      // Track contact loading errors
      datadogRum.addAction('fmcsa_contacts_error', {
        component: 'ContactAutocomplete',
        surface,
        carrier_dot_number: dotNumber,
        error_message: error.message,
      })
    },
  })

  const handleOptionSubmit = (contact) => {
    setSelectedContact(contact)
    setSearchTerm('')
    // Update input to show only the contact name (no fallback to phone/email to prevent semantic confusion)
    const contactInfo = contact?.contactName || ''

    // Track contact selection
    datadogRum.addAction('fmcsa_contact_selected', {
      component: 'ContactAutocomplete',
      surface,
      search_term: inputDebounced,
      carrier_dot_number: dotNumber,
      carrier_name: carrierNameOf(carrierData?.carrierByDotNumber),
      contact_name: contact?.contactName || 'Unnamed Contact',
      contact_id: contact?.contactId,
    })

    if (onChange) {
      onChange(contactInfo)
    }

    if (onSelect) {
      onSelect(contact)
    }
    combobox.closeDropdown()
  }

  // Filter contacts based on search input
  const filterContacts = (contacts, searchTerm) => {
    if (!searchTerm.trim()) {
      return contacts
    }

    const searchLower = searchTerm.toLowerCase()
    return contacts.filter((contact) => {
      const name = (contact.contactName || '').toLowerCase()
      const phone = (contact.phoneNumber || '').toLowerCase()
      const email = (contact.email || '').toLowerCase()

      return (
        name.includes(searchLower) ||
        phone.includes(searchLower) ||
        email.includes(searchLower)
      )
    })
  }

  // Sync inputDebounced with external value prop when component is controlled
  useEffect(() => {
    if (props.value !== undefined) {
      setInputDebounced(String(props.value))
    }
  }, [props.value, setInputDebounced])

  // Load contacts when dotNumber changes
  useEffect(() => {
    if (dotNumber) {
      getCarrierByDotNumber({ variables: { dotNumber } })
    }
  }, [dotNumber, getCarrierByDotNumber])

  // Reachable contacts for the current DOT. Empty without a DOT, so a cleared
  // DOT does not keep showing the previous carrier's count
  const knownContacts = useMemo(
    () =>
      dotNumber
        ? reachableContacts(carrierData?.carrierByDotNumber?.contacts)
        : [],
    [carrierData, dotNumber]
  )

  // Filter contacts when the recruiter types
  useEffect(() => {
    const filtered = filterContacts(knownContacts, searchTerm)
    setFilteredContacts(filtered)
  }, [searchTerm, knownContacts])

  // Track manual entry when user types without selecting from contacts
  useEffect(() => {
    if (
      props.value &&
      String(props.value).length >= 2 &&
      !_selectedContact &&
      filteredContacts.length === 0 &&
      !loadingContacts &&
      inputDebounced === String(props.value)
    ) {
      // User has typed a value but no contacts were found and no selection was made
      datadogRum.addAction('fmcsa_manual_entry', {
        component: 'ContactAutocomplete',
        surface,
        manual_input: props.value,
        search_attempted: inputDebounced.length >= 2,
        carrier_dot_number: dotNumber,
        search_type: 'contact_search',
      })
    }
  }, [
    props.value,
    _selectedContact,
    filteredContacts.length,
    loadingContacts,
    inputDebounced,
    dotNumber,
    surface,
  ])

  const renderDropdownContent = () => {
    if (loadingContacts) {
      return (
        <Combobox.Empty className="pt-2 text-center text-xs">
          <Loader size={18} color="gray" />
        </Combobox.Empty>
      )
    }

    if (contactsError) {
      return (
        <Combobox.Empty className="flex flex-row items-center justify-center gap-2 pt-2 text-xs text-doubleNickel-warning-500">
          <CircleAlert size={14} className="stroke-doubleNickel-warning-500" />
          Error loading contacts
        </Combobox.Empty>
      )
    }

    if (!dotNumber) {
      return (
        <Combobox.Empty className="pt-2 text-center text-xs text-doubleNickel-gray-500">
          Provide a DOT number to automatically load contacts
        </Combobox.Empty>
      )
    }

    if (filteredContacts.length === 0) {
      const hasSearchTerm = searchTerm.trim().length > 0
      return (
        <Combobox.Empty className="pt-2 text-center text-xs text-doubleNickel-gray-500">
          {hasSearchTerm
            ? 'No contacts match your search'
            : `0 contacts for DOT ${dotNumber}`}
        </Combobox.Empty>
      )
    }

    return filteredContacts.map((contact, index) => (
      <ContactOption
        key={`${contact.contactId}-${index}`}
        contact={contact}
        index={index}
      />
    ))
  }

  return (
    <Combobox
      onOptionSubmit={handleOptionSubmit}
      store={combobox}
      withinPortal={true}
      position="bottom-start"
      disabled={disabled}
      offset={5}
    >
      <Combobox.Target>
        <TextInput
          ref={mergedInputRef}
          label={label}
          placeholder={placeholder || formatContactCount(knownContacts.length)}
          disabled={disabled}
          value={inputDebounced}
          classNames={
            classNames || {
              root: 'flex-1',
              label: 'text-xs font-medium text-doubleNickel-gray-700',
              input: `cursor-pointer [&[data-disabled]]:bg-doubleNickel-white [&[data-disabled]]:opacity-100 [&[data-disabled]]:text-doubleNickel-gray-600`,
            }
          }
          rightSectionPointerEvents="auto"
          rightSection={
            // What we hold for this DOT, not what the search matched. None is
            // not an error, so it is grey rather than red. A button, so the
            // icon is a second way into the list
            <UnstyledButton
              aria-label={formatContactCount(knownContacts.length)}
              title={formatContactCount(knownContacts.length)}
              disabled={disabled || !dotNumber}
              className="flex h-full items-center justify-center rounded-sm px-1 hover:bg-doubleNickel-gray-100 disabled:cursor-default disabled:hover:bg-transparent"
              onMouseDown={(event) => {
                // Keep focus in the input so the blur handler does not
                // close the list we are about to open
                event.preventDefault()
              }}
              onClick={() => {
                ownInputRef.current?.focus()
                openContacts()
              }}
            >
              {knownContacts.length > 1 ? (
                <Users size={12} className="text-doubleNickel-success-600" />
              ) : knownContacts.length === 1 ? (
                <User size={12} className="text-doubleNickel-success-600" />
              ) : (
                <UserRoundX size={12} className="text-doubleNickel-gray-400" />
              )}
            </UnstyledButton>
          }
          onChange={(event) => {
            const newValue = event.currentTarget.value
            if (onChange) {
              onChange(newValue)
            }
            setInputDebounced(newValue)
            setSearchTerm(newValue)
            // Reset selection when user starts typing
            setSelectedContact(null)
            combobox.resetSelectedOption()
            combobox.openDropdown()
          }}
          onClick={(event) => {
            if (!disabled && dotNumber) {
              openContacts()
              event.stopPropagation()
            }
          }}
          onFocus={openContacts}
          onBlur={() => combobox.closeDropdown()}
          {...(props as Omit<TextInputProps, 'onChange' | 'value'>)}
        />
      </Combobox.Target>

      <Combobox.Dropdown
        styles={{ dropdown: { maxWidth: '100vh' } }}
        translate="no"
      >
        <Combobox.Options mah={300} className="overflow-y-auto">
          {renderDropdownContent()}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default ContactAutocomplete
